import { useTranslation } from 'next-i18next'
import { useNextSanityImage } from 'next-sanity-image'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { z } from 'zod'

import ListItem from '@nxt-insurance/ui-kit/components/listComponents/ListItem'
import { ICON_SIZE } from '@nxt-insurance/ui-kit/constants'

import { runQuery, sanityClientLike } from '@/cms/client'
import type { Product } from '@/cms/contentBlock'
import { abstractProductQuery, mgaLightProductQuery } from '@/cms/contentBlock'
import type { ImageType } from '@/cms/helperTypes'
import { Icon } from '@/components/Icon'
import { APP_ENV } from '@/config/env'
import { EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { useFormatting } from '@/context/FormattingContext'
import { reportErrorMessage } from '@/services/errorReporting'
import { getLocaleParts } from '@/utils/locale'
import { getPathWithParams, removeImpermanentSearchParams } from '@/utils/url'
import type { PortableTextTypeComponentProps } from '@portabletext/react'
import { useQuery } from '@tanstack/react-query'

const productListSchema = z.object({
  _type: z.literal('productList'),
  products: z
    .array(
      z.object({
        _key: z.string(),
        _ref: z.string(),
        _type: z.enum(['abstractProduct', 'mgaLightProduct']),
      })
    )
    .optional(),
})

const ProductItem = (props: {
  id: string
  title: string
  image: ImageType
  price?: number
  website: Product['website']
  availableInEnvironments: Product['availableInEnvironments']
  sectionName: string
}) => {
  const { track } = useAnalytics()
  const imageProps = useNextSanityImage(sanityClientLike, props.image, {
    imageBuilder: (imageUrlBuilder) => imageUrlBuilder.width(ICON_SIZE.large),
  })
  const { query } = useRouter()
  const { t } = useTranslation()
  const href = useMemo(() => {
    const whitelistedParams = removeImpermanentSearchParams(query).toString()
    const { folder, slug } = props.website
    const url = [folder, slug.replace('index', '')].filter(Boolean).join('/')
    return getPathWithParams(`/${url}`, { params: whitelistedParams })
  }, [props.website, query])
  const formatter = useFormatting()
  if (APP_ENV === 'production' && !props.availableInEnvironments?.includes(APP_ENV)) {
    return null
  }

  return (
    <Link href={href}>
      <ListItem
        id={props.id}
        title={props.title}
        image={
          <Image
            {...imageProps}
            blurDataURL={props.image.asset.metadata?.lqip}
            placeholder="blur"
            alt={props.title}
          />
        }
        subtitle={
          t('price.minPerMonth', {
            minMonthlyPremium:
              props.price !== undefined ? formatter.formatPrice(props.price) : undefined,
          }) ?? undefined
        }
        imageSize="large"
        paddingY="none"
        onPress={() => {
          track(EVENTS.ProductListClicked, {
            productKey: props.id,
            sectionName: props.sectionName,
          })
        }}
        icon={<Icon size="small" icon={{ name: 'action.chevronRight_24' }} />}
      />
    </Link>
  )
}

const AbstractProduct = (props: { id: string; sectionName: string }) => {
  const { locale } = useRouter()
  const [lang] = getLocaleParts(locale)
  const { data: product } = useQuery({
    queryKey: ['abstractProduct', props.id, lang],
    queryFn: async () =>
      runQuery(abstractProductQuery, {
        lang,
        id: props.id,
      }),
    throwOnError: true,
  })
  if (!product) {
    return null
  }
  return (
    <ProductItem
      id={product.defaultProduct.productKey.productKey}
      title={product.defaultProduct.title}
      image={product.defaultProduct.imageSquare}
      price={product.defaultProduct.minTotalMonthlyPremium ?? undefined}
      availableInEnvironments={product.availableInEnvironments}
      website={product.website}
      sectionName={props.sectionName}
    />
  )
}

const MgaLightProduct = (props: { id: string; sectionName: string }) => {
  const { locale } = useRouter()
  const [lang] = getLocaleParts(locale)
  const { data: product } = useQuery({
    queryKey: ['mgaLightProduct', props.id, lang],
    queryFn: async () =>
      runQuery(mgaLightProductQuery, {
        lang,
        id: props.id,
      }),
    throwOnError: true,
  })
  if (!product) {
    return null
  }
  return (
    <ProductItem
      id={product.mgaLightProductKey}
      title={product.title}
      image={product.imageSquare}
      price={product.minMonthlyPremium ?? undefined}
      availableInEnvironments={product.availableInEnvironments}
      website={product.website}
      sectionName={props.sectionName}
    />
  )
}

export const ProductList = (props: {
  node: PortableTextTypeComponentProps<unknown>
  sectionName: string
}) => {
  const value = productListSchema.parse(props.node.value)
  if (!value.products) {
    return null
  }
  return (
    <div className="flex flex-col self-stretch">
      <ul className="flex flex-col gap-y-5">
        {value.products.map((product) => {
          if ('_type' in product) {
            if (product._type === 'abstractProduct') {
              return (
                <AbstractProduct
                  key={product._ref}
                  id={product._ref}
                  sectionName={props.sectionName}
                />
              )
            } else if (product._type === 'mgaLightProduct') {
              return (
                <MgaLightProduct
                  key={product._ref}
                  id={product._ref}
                  sectionName={props.sectionName}
                />
              )
            }
          }
          reportErrorMessage('Assertion: Unknown product type in ProductList', product)
          return null
        })}
      </ul>
    </div>
  )
}
